import React, { useEffect, useCallback } from "react";
import { Controller } from "react-hook-form";
import {
  Typography,
  Stack,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormLabel,
  Button,
  Autocomplete,
  Box,
  Dialog,
  DialogContent,
} from "@mui/material";
import { Tag } from "src/components/Tag";
import Section from "src/components/Section";
import CustomStepper from "src/components/CustomStepper";
import PageHeader from "src/components/PageHeader";
import { SELECT_MENU_MAX_HEIGHT } from "src/styles/const";
import { cityList, districtList, getYearOptions, PROPERTY_TYPES, STATION_NAMES } from "src/utils/options";
import { useHowmaBuildingsSubmit, useHowmaRoomSubmit, useInputPage } from "./hooks";
import { useLocation, useNavigate } from "react-router";
import { usePropertyStep } from "src/hooks/usePropertyStep";
import { useSetAtom } from "jotai/react";
import { residenceFlagAtom } from "src/utils/flags";
import { useGetCities } from "src/hooks/address";
import { HowMaBuilding } from "src/api/generated/lp";
import {
  convertAreaNameToCity,
  convertAreaNameToPrefecture,
  convertStationNameToListName,
} from "src/utils/HowMaBuildingSearchApiLogic";

function dedupeById<T extends { id: number }>(arr: T[]): T[] {
  const seen = new Set<number>();
  return arr.filter((x) => (seen.has(x.id) ? false : (seen.add(x.id), true)));
}

export default function InputPropertyPage() {
  const { control, errors, setValue } = useInputPage();
  const navigate = useNavigate();
  const location = useLocation();
  const { validatePropertyStep } = usePropertyStep();

  const setResidenceFlag = useSetAtom(residenceFlagAtom);
  const { cities, loadingCities } = useGetCities();

  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const [buildingOptions, setBuildingOptions] = React.useState<HowMaBuilding[]>([]);
  const { sending: searchLoading, results: apiResults, search, clear } = useHowmaBuildingsSubmit(300);
  const { searchRoom, resultsRoom, firstRoomM2 } = useHowmaRoomSubmit(300);

  // API結果が来たら重複除去してからオプションに反映（name重複でもidで一意に）
  useEffect(() => {
    setBuildingOptions(dedupeById(apiResults ?? []));
  }, [apiResults]);

  // 入力に応じて建物検索
  const buildingsSearch = useCallback(
    (query: string) => {
      const q = query?.trim();
      if (!q || q.length < 2) {
        setBuildingOptions([]);
        clear();
        return;
      }
      search(q);
    },
    [search, clear],
  );

  // 部屋詳細API
  const fetchRoomOptions = useCallback(
    async (buildingId: number) => {
      searchRoom(buildingId);
    },
    [searchRoom],
  );

  // 部屋結果が到着したら area を更新（値が無いなら 40）
  useEffect(() => {
    if (!resultsRoom || resultsRoom.length === 0) return;
    setValue("area", String(firstRoomM2 ?? 40), { shouldValidate: true });
  }, [resultsRoom, firstRoomM2, setValue]);

  // 候補選択時の自動入力
  const setSelectedHowMaBuilding = useCallback(
    (b: HowMaBuilding) => {
      // マンション名
      setValue("building_name", b.name, { shouldValidate: true, shouldDirty: true });

      // 東京限定判定
      const tokyoOnly = ["東京都"];
      const prefecture = convertAreaNameToPrefecture(b.area.name, tokyoOnly);
      if (prefecture !== "東京都") {
        setIsModalOpen(true);
      }

      // 市区町村と地区の抽出（地区は都道府県・市区町村を除いた残りから）
      const city = convertAreaNameToCity(b.area.name, cityList);

      let districtTarget = b.area.name;
      if (prefecture) districtTarget = districtTarget.replace(prefecture, "");
      if (city) districtTarget = districtTarget.replace(city, "");
      districtTarget = districtTarget.trim();

      const district = convertAreaNameToCity(districtTarget, districtList);

      setValue("prefecture", prefecture, { shouldValidate: true });
      setValue("city_ward_town_village", city, { shouldValidate: true });
      setValue("district", district, { shouldValidate: true });

      // 築年
      if (b.built_at_year != null) {
        setValue("construction_year", String(b.built_at_year), { shouldValidate: true });
      }

      // 最寄駅
      if (b.station?.name != null) {
        const station = convertStationNameToListName(b.station?.name, STATION_NAMES);
        setValue("station_name", station, { shouldValidate: true });
      }
    },
    [setValue],
  );

  // 送信
  const handleFormSubmit = useCallback(async () => {
    // クライアント側での固定値
    setValue("structure", "RC");
    setValue("actual_sales_price", "20");

    const isValid = await validatePropertyStep();
    if (isValid) {
      navigate({
        pathname: "/input_personal",
        search: location.search,
      });
    }
  }, [navigate, location.search, setValue, validatePropertyStep]);

  return (
    <>
      <PageHeader title="高精度不動産AI査定" px={5}>
        <Typography fontWeight="bold" textAlign="center">
          以下フォームをご入力ください。
          <br />
          不動産ビッグデータを学習した人工知能 ( AI ) による査定価格をお伝えします。
        </Typography>
      </PageHeader>

      {/* フォームセクション */}
      <Section sx={{ pb: 5 }}>
        <Stack spacing={4}>
          <CustomStepper activeStep={0} />

          <Stack spacing={3}>
            <Typography variant="h6" fontWeight="bold">
              物件概要
            </Typography>

            {/* 物件種別 */}
            <FormControl>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FormLabel>物件種別</FormLabel>
                  <Tag label="必須" color="secondary" />
                </Stack>
                <Controller
                  name="property_type"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value ?? ""}
                      displayEmpty
                      onChange={(e) => {
                        const value = e.target.value as string;
                        field.onChange(value);
                        setResidenceFlag(value === "residence");
                      }}
                      renderValue={(selected) =>
                        selected ? PROPERTY_TYPES.find((t) => t.key === selected)?.label : "選択してください"
                      }
                      error={!!errors.property_type}
                      MenuProps={{ slotProps: { paper: { sx: { maxHeight: SELECT_MENU_MAX_HEIGHT } } } }}
                    >
                      <MenuItem value="">選択してください</MenuItem>
                      {PROPERTY_TYPES.map((type) => (
                        <MenuItem key={type.key} value={type.key}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.property_type && (
                  <Typography color="error" variant="caption">
                    {errors.property_type.message}
                  </Typography>
                )}
              </Stack>
            </FormControl>

            {/* マンション名（検索→自動入力） */}
            <FormControl>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FormLabel>マンション・アパート名</FormLabel>
                  <Tag label="必須" color="secondary" />
                </Stack>

                <Controller
                  name="building_name"
                  control={control}
                  rules={{ required: "入力してください" }}
                  render={({ field }) => (
                    <Autocomplete
                      size="small"
                      freeSolo
                      disableClearable
                      options={buildingOptions}
                      loading={searchLoading}
                      value={field.value ?? ""}
                      getOptionLabel={(option: HowMaBuilding | string) =>
                        typeof option === "string" ? option : option.name
                      }
                      renderOption={(props, option: HowMaBuilding) => (
                        <li {...props} key={`b-${option.id}`}>
                          <Box>
                            <Typography variant="body2" component="div">
                              {option.name}
                            </Typography>
                            <Typography variant="caption">({option.area.name})</Typography>
                          </Box>
                        </li>
                      )}
                      // value 比較
                      isOptionEqualToValue={(opt, val) =>
                        typeof val !== "string" && typeof opt !== "string" ? opt.id === val.id : false
                      }
                      // 候補選択
                      onChange={(_, selected) => {
                        if (typeof selected === "string") return;
                        field.onChange(selected.name);
                        setSelectedHowMaBuilding(selected);
                        fetchRoomOptions(selected.id);
                      }}
                      // 入力時のみ検索
                      onInputChange={(_, inputed, reason) => {
                        if (reason !== "input") return;
                        field.onChange(inputed);
                        buildingsSearch(inputed);
                      }}
                      onFocus={() => {
                        const q = (field.value ?? "").toString();
                        if (q) buildingsSearch(q);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="マンション・アパート名"
                          helperText="マンション・アパート名から物件情報を自動入力できます"
                          error={!!errors.building_name}
                        />
                      )}
                    />
                  )}
                />
                {errors.building_name && (
                  <Typography color="error" variant="caption">
                    {errors.building_name.message}
                  </Typography>
                )}
              </Stack>
            </FormControl>

            {/* 市区町村 */}
            <FormControl>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FormLabel>市区町村</FormLabel>
                  <Tag label="必須" color="secondary" />
                </Stack>
                <Controller
                  name="city_ward_town_village"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={cities ?? []}
                      loading={loadingCities}
                      value={field.value || null}
                      onChange={(_, value) => field.onChange(value ?? "")}
                      isOptionEqualToValue={(opt, val) => opt === val}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="選択してください"
                          error={!!errors.city_ward_town_village}
                          helperText={errors.city_ward_town_village?.message}
                        />
                      )}
                    />
                  )}
                />
              </Stack>
            </FormControl>

            {/* 地区名 */}
            <FormControl>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FormLabel>地区名</FormLabel>
                  <Tag label="必須" color="secondary" />
                </Stack>
                <Controller
                  name="district"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={districtList}
                      value={field.value || null}
                      onChange={(_, value) => field.onChange(value ?? "")}
                      isOptionEqualToValue={(opt, val) => opt === val}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="地区名を選択"
                          error={!!errors.district}
                          helperText={errors.district?.message}
                        />
                      )}
                    />
                  )}
                />
              </Stack>
            </FormControl>

            {/* 最寄駅 */}
            <FormControl>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FormLabel>最寄駅</FormLabel>
                  <Tag label="必須" color="secondary" />
                </Stack>
                <Controller
                  name="station_name"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={STATION_NAMES}
                      value={field.value || null}
                      onChange={(_, value) => field.onChange(value ?? "")}
                      isOptionEqualToValue={(opt, val) => opt === val}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="駅名を選択"
                          error={!!errors.station_name}
                          helperText={errors.station_name?.message}
                        />
                      )}
                    />
                  )}
                />
              </Stack>
            </FormControl>

            {/* 駅徒歩（分） */}
            <FormControl>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FormLabel>駅徒歩（分）</FormLabel>
                  <Tag label="必須" color="secondary" />
                </Stack>
                <Controller
                  name="station_walk_minutes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="number"
                      slotProps={{ htmlInput: { min: 0 } }}
                      error={!!errors.station_walk_minutes}
                      helperText={errors.station_walk_minutes?.message}
                    />
                  )}
                />
              </Stack>
            </FormControl>

            {/* 専有面積 */}
            <FormControl>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FormLabel>専有面積（㎡）</FormLabel>
                  <Tag label="必須" color="secondary" />
                </Stack>
                <Controller
                  name="area"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} error={!!errors.area} helperText={errors.area?.message} />
                  )}
                />
              </Stack>
            </FormControl>

            {/* 築年 */}
            <FormControl>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FormLabel>築年</FormLabel>
                  <Tag label="必須" color="secondary" />
                </Stack>
                <Controller
                  name="construction_year"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value ?? ""}
                      displayEmpty
                      error={!!errors.construction_year}
                      renderValue={(selected) => (selected ? selected : "選択してください")}
                      MenuProps={{ slotProps: { paper: { sx: { maxHeight: SELECT_MENU_MAX_HEIGHT } } } }}
                    >
                      <MenuItem value="">選択してください</MenuItem>
                      {getYearOptions().map((option) => (
                        <MenuItem key={`y-${option}`} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.construction_year && (
                  <Typography color="error" variant="caption">
                    {errors.construction_year.message}
                  </Typography>
                )}
              </Stack>
            </FormControl>
          </Stack>

          <Button variant="contained" color="primary" onClick={handleFormSubmit}>
            次へ
          </Button>
          <Button
            fullWidth
            sx={{ backgroundColor: "#B1B1B1" }}
            onClick={() =>
              navigate({
                pathname: "/",
                search: location.search,
              })
            }
          >
            戻る
          </Button>
        </Stack>
      </Section>

      {/* 東京限定ダイアログ */}
      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          <Stack spacing={2}>
            <Typography color="primary.dark" variant="h6" fontWeight="bold" textAlign="center">
              ごめんなさい！東京限定なんです
            </Typography>
            <Typography variant="body1" textAlign="center">
              現在、このサービスは東京都内の物件だけを査定しています。
              <br />
              東京の物件を入力いただければ、すぐに結果をお届けできます！
            </Typography>
            <Button variant="contained" color="primary" fullWidth onClick={() => setIsModalOpen(false)}>
              とじる
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
